import { Injectable } from '@angular/core';
import _ from 'lodash';
import { first } from 'rxjs/operators';
import { BxLogger } from '../../../core/services/bx-logger.service';
import { AsideModalSize, HelperService } from '../../../core/services/helper.service';
import { LaunchDarklyService } from '../../../core/services/launchdarkly.service';
import { Job, PurchaseOrder, PurchaseOrderInvoice } from '../../../data/models';
import { IntegrationProvider } from '../../../data/models/integrations.model';
import { ContactLegacy } from '../../../data/repositories/contacts/contacts-legacy-convertor.service';
import { JobsInvoiceSettingsService } from '../../../data/repositories/jobs/jobs-invoice-settings.service';
import { RepoCommonService } from '../../../data/repositories/repo-common.service';
import { RepoContactsService } from '../../../data/repositories/repo-contacts.service';
import { RepoJobsService } from '../../../data/repositories/repo-jobs.service';
import { BreezeService } from '../../../data/services/breeze/breeze.service';
import { SharedDataService } from '../../../data/services/shared-data.service';
import { ReportService } from '../../../reports/report.service';
import { MessagesService } from '../../messages/messages.service';
import { AccountingImportService } from '../accounting-import/accounting-import.service';
import { PurchaseOrderDetailComponent } from './purchase-order-detail.component';
import { PurchaseOrdersServiceBase } from './purchase-orders.base.service';
import { OrderTermsComponent } from './terms/order-terms.component';
import { Uuid } from '../../../../graphql';

export type ImportSettings = {
    copyAttachmentsOnCreate: boolean;
    invoiceId: string;
    provider: IntegrationProvider;
    importingIntoExistingOrder: boolean;
};

@Injectable({ providedIn: 'root' })
export class PurchaseOrdersService extends PurchaseOrdersServiceBase {
    constructor(
        private helperService: HelperService,
        private reportService: ReportService,
        private messagesService: MessagesService,
        lds: LaunchDarklyService,
        breezeService: BreezeService,
        sharedData: SharedDataService,
        repoJobs: RepoJobsService,
        private repoContacts: RepoContactsService,
        repoCommon: RepoCommonService,
        private accountingImportService: AccountingImportService,
        jobsSettingsService: JobsInvoiceSettingsService,
        logger: BxLogger
    ) {
        super(breezeService, lds, sharedData, repoJobs, repoCommon, jobsSettingsService, logger);
    }

    async emailOrder(order: PurchaseOrder) {
        const report = await this._getEmailReport(order);
        if (order.ContactViaGql) {
            const contactPersons = await this.repoContacts.GetContactPersons(order.ID_Contact);
            order.ContactViaGql.ContactPersons = contactPersons.results;
        }
        const allContacts = await this.repoContacts.GetEmailSMSPersons((order.Contact as unknown as ContactLegacy) ?? order.ContactViaGql);
        let to = allContacts.email;
        let contact = allContacts.email;
        const referenceId = (order.Contact ?? order.ContactViaGql)?.ID_Contact;
        const contactName =
            order.SupplierAttention ||
            (order.Contact ?? order.ContactViaGql)?.Name! ||
            (order.Contact ?? order.ContactViaGql)?.BusinessName!;
        // If the order has a contact, use that one

        if (allContacts.email && allContacts.email.length) {
            const person = _.find(allContacts.email, { name: contactName });
            if (person) {
                // @ts-ignore TS2322
                to = [person];
                // @ts-ignore TS2322
                contact = [person];
            }
        }
        const result = await this.messagesService.sendEmail({
            To: to,
            contact: contact,
            ReferenceId: referenceId,
            Subject: order.OrderNumber
                ? order.Job.FullDescription + ' - Order #' + order.OrderNumberString
                : order.Job.FullDescription + ' - Orders',
            Body: 'Dear ' + _.escape(contactName) + ',',
            // @ts-ignore TS2532
            StorageAttachments: [report.data],
            references: [
                {
                    Type: 'job',
                    Id: order.ID_Reference,
                },
                {
                    Type: 'contact',
                    Id: (order.Contact ?? order.ContactViaGql)?.ID_Contact,
                },
                {
                    Type: 'estimate',
                    Id: order.Job.ID_Estimate,
                },
                {
                    Type: 'purchaseorder',
                    Id: order.ID_PurchaseOrder,
                },
            ],
            context: 'purchaseorder',
        });
        if (!result.cancelled && result.sent) {
            if (order.OrderStatus === 'Unsent') {
                order.OrderStatus = 'Sent';
                order.SentAt = new Date();
                await this.repoJobs.Save(order, 'Order sent');
                await this.syncOrder(order);
            }
        }
    }

    private _getEmailReport(order: PurchaseOrder) {
        let params = null;

        if (order.OrderType === 'purchase') {
            params = {
                reportName: 'purchaseorder',
                reportTitle: 'Purchase Order Report',
                params: {
                    ID_PurchaseOrder: order.ID_PurchaseOrder,
                },
                format: 'pdf',
                fileName: (order.Job.Number ? order.Job.Number + '-' : '') + 'PurchaseOrder-' + order.OrderNumberString,
            };
        } else if (order.OrderType === 'work') {
            params = {
                reportName: 'purchaseorder',
                reportTitle: 'Work Order Report',
                params: {
                    ID_PurchaseOrder: order.ID_PurchaseOrder,
                },
                format: 'pdf',
                fileName: (order.Job.Number ? order.Job.Number + '-' : '') + 'WorkOrder-' + order.OrderNumberString,
            };
        }

        // @ts-ignore TS2345
        return this.reportService.generate(params);
    }

    printOrder(order: PurchaseOrder) {
        const orderType = this.repoJobs.GetPurchaseOrderType(order.OrderType);
        let params = null;

        if (order.OrderType === 'purchase') {
            params = {
                reportName: 'purchaseorder',
                // @ts-ignore TS2532
                reportTitle: orderType.name,
                params: {
                    ID_PurchaseOrder: order.ID_PurchaseOrder,
                },
                fileName: (order.Job.Number ? order.Job.Number + '-' : '') + 'PurchaseOrder-' + order.OrderNumberString,
            };
        } else if (order.OrderType === 'work') {
            params = {
                reportName: 'purchaseorder',
                // @ts-ignore TS2532
                reportTitle: orderType.name,
                params: {
                    ID_PurchaseOrder: order.ID_PurchaseOrder,
                },
                fileName: (order.Job.Number ? order.Job.Number + '-' : '') + 'WorkOrder-' + order.OrderNumberString,
            };
        }

        // @ts-ignore TS2345
        return this.reportService.generate(params);
    }

    async getPurchaseOrder(id: string, jobId: string) {
        const data = await this.repoJobs.GetJobPurchaseOrder(id, jobId);
        return data.results[0];
    }

    async viewOrderById(purchaseOrderId: Uuid, referenceId: Uuid, importSettings?: ImportSettings) {
        const purchaseOrder = await this.loadOrderById(purchaseOrderId, referenceId);
        return await this._viewOrder(purchaseOrder, importSettings);
    }

    async viewOrder(order: PurchaseOrder, importSettings?: ImportSettings) {
        // ensure order invoices are loaded
        const purchaseOrder = await this.loadOrder(order);
        return await this._viewOrder(purchaseOrder, importSettings);
    }

    private async _viewOrder(purchaseOrder: PurchaseOrder, importSettings?: ImportSettings) {
        const initialState = {
            purchaseOrder,
            importSettings,
        } as Partial<PurchaseOrderDetailComponent>;
        const modalRef = this.helperService.showModalAside(PurchaseOrderDetailComponent, { initialState }, AsideModalSize.wide);
        // @ts-ignore TS2532
        const result = await modalRef.content.action.pipe(first()).toPromise();
        if (result.cancelled) {
            this.cancelOrder(purchaseOrder);
            throw result;
        } else {
            await this.setOrderNumber(purchaseOrder);
            await this.saveOrder(purchaseOrder);
            await this.syncOrder(purchaseOrder);
            await this.sendOrder(purchaseOrder);
            if (importSettings?.copyAttachmentsOnCreate) {
                this.accountingImportService
                    .copyAttachmentsFromExternalInvoices(importSettings.invoiceId, importSettings.provider, purchaseOrder.ID_PurchaseOrder)
                    .subscribe((result) => {
                        if (
                            result.errors ||
                            result.data?.copyAttachmentsFromExternalInvoices.partialSuccessOfCopiedAccountingAttachment?.failedCount
                        ) {
                            const errors =
                                result.errors?.map((e) => e.message) ??
                                result.data?.copyAttachmentsFromExternalInvoices.partialSuccessOfCopiedAccountingAttachment?.failed.map(
                                    (f) => `${f.fileName} failed to copy.`
                                );
                            errors?.forEach((error) => this.logger.error(error));
                        } else {
                            this.logger.success(`Provider Attachments copied for ${purchaseOrder.Description || 'PO/WO'}`);
                        }
                    });
            }
            return purchaseOrder;
        }
    }

    private async loadOrder(order: PurchaseOrder): Promise<PurchaseOrder> {
        // No need to reload a new order
        if (order.entityAspect.entityState.isAdded()) {
            return order;
        } else {
            return await this.loadOrderById(order.ID_PurchaseOrder, order.ID_Reference as Uuid);
        }
    }

    private async loadOrderById(purchaseOrderId: Uuid, referenceId: Uuid) {
        const data: any = await this.repoJobs.GetJobPurchaseOrder(purchaseOrderId, referenceId);
        await this.repoJobs.GetPurchaseOrderInvoices(purchaseOrderId);
        return data.results[0] as PurchaseOrder;
    }

    private async sendOrder(order: PurchaseOrder) {
        if (order.send) {
            delete order.send;
            await this.emailOrder(order);
        } else return order;
    }

    async newOrder(job: Job, initData?: any, items?: any[], invoice?: any, importSettings?: ImportSettings) {
        const order = this.repoJobs.CreatePurchaseOrder(job, initData);
        let count = 0;
        let newInvoice: PurchaseOrderInvoice | undefined;

        if (invoice) {
            newInvoice = this.repoJobs.CreatePurchaseOrderInvoice(order, invoice);
        }

        if (items) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const orderItem = this.repoJobs.CreatePurchaseOrderItem(order, item);
                orderItem.Order = count;
                orderItem.CostItemType = item.CostItemType;
                orderItem.TaxContent = item.TaxContent;
                orderItem.TaxRates = item.TaxRates;
                count = count + 1;
                // refresh totals
                orderItem.calculateTotal();

                if (newInvoice) {
                    const invoiceItem = {
                        Quantity: orderItem.Quantity,
                        UnitCost: orderItem.UnitCost,
                        TaxRates: orderItem.TaxRates,
                        TaxContent: orderItem.TaxContent,
                        CostItemType: orderItem.CostItemType,
                    };

                    const newInvoiceItem = this.repoJobs.CreatePurchaseOrderInvoiceItem(newInvoice, invoiceItem);
                    newInvoiceItem.ID_PurchaseOrderItem = orderItem.ID_PurchaseOrderItem;

                    if (importSettings !== null) newInvoiceItem.TaxRates = orderItem.TaxRates;

                    newInvoiceItem.calculateTotal();
                }
            }
        }

        if (newInvoice) {
            newInvoice.calculateTotal();
        }

        return await this.viewOrder(order, importSettings);
    }

    viewTermsAndConditions(orderType: string) {
        return new Promise<void>((resolve, reject) => {
            const initialState = {
                type: orderType,
            };

            const modalRef = this.helperService.showModalAside(OrderTermsComponent, { initialState });
            // @ts-ignore TS2532
            modalRef.content.action.pipe(first()).subscribe((result) => {
                if (!result.cancelled) resolve();
                else reject();
            });
        });
    }
}
